/* list-unused-translation-keys:disable */
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

type Props = {
  /** Translation key. Defined in the translation file e.g.: as { "key": { "other": "translation" } } and passed here as "key". */
  id: string;
  /** Value to determine the plural. Should be used as '{count}' in case of interpolation. */
  count: number;
  /** Use 'cardial' (default) for regular numbers, and 'ordinal' for e.g.: 1st, 2nd, 3rd. */
  type?: 'cardinal' | 'ordinal';
};

/** For using with pluralized keys. See the "Pluralization" section in docs/pluralization.md for detailed instructions. */
const usePluralization = () => {
  const intl = useIntl();

  const FormattedPluralMessage = ({ id, count, type = 'cardinal' }: Props) => {
    const defaultMessage = useMemo(() => {
      const hasFallbackId = !!intl.messages[`${id}.other`];
      return hasFallbackId ? intl.formatMessage({ id: `${id}.other` }) : undefined;
    }, [id]);

    const pluralRule = intl.formatPlural(count, { type });
    return <FormattedMessage id={`${id}.${pluralRule}`} defaultMessage={defaultMessage} values={{ count }} />;
  };

  const formatPluralMessage = ({ id, count, type = 'cardinal' }: Props) => {
    const pluralRule = intl.formatPlural(count, { type });
    const hasFallbackId = !!intl.messages[`${id}.other`];
    const defaultMessage = hasFallbackId ? intl.formatMessage({ id: `${id}.other` }) : undefined;
    return intl.formatMessage({ id: `${id}.${pluralRule}`, defaultMessage }, { count });
  };

  return { FormattedPluralMessage, formatPluralMessage };
};

export default usePluralization;
